import {
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	Output
} from '@angular/core';
import { Observable, of, Subject, timer } from 'rxjs';
import { catchError, exhaustMap, takeUntil } from 'rxjs/operators';
import { AnaAssistantService } from '@brand-lolly/app/_services/ana-assistant.service';
import {
	AnaMarketplaceSearchResponse,
	AnaMarketplaceSearchStatus
} from '@brand-lolly/app/_models/ana-assistant.model';

/** The AI Search watcher's cadence, so both waits read the same. */
const POLL_INTERVAL_MS = 3000;

/** One dropped read is a blip; three in a row is losing sight of the search. */
const MAX_CONSECUTIVE_POLL_FAILURES = 3;

const ACTIVE_STATUSES: AnaMarketplaceSearchStatus[] = ['queued', 'running'];

/**
 * How a watched Marketplace search ended. `status` is null when the search
 * could not be read at all — the watcher lost it, nothing says it finished.
 */
export interface AnaMarketplaceSearchEnd {
	status: AnaMarketplaceSearchStatus | null;
	savedCount: number;
}

/**
 * The offscreen watcher of a Marketplace search Ana started.
 *
 * The sibling of `app-ana-search-progress` for the other kind of search: it
 * draws nothing — the working line in the conversation is the running signal —
 * and polls `GET /assistant/marketplace-searches/{id}` until the search reaches
 * a terminal status. The server has already written the plan by then; this
 * only says WHEN, so the facade can hand the turn back to Ana.
 *
 * Inputs: `searchId` — the search to watch; null stops the poll.
 * Emits: `searchEnded` once per search, terminal or lost; `searchChecking`
 * with the number of creators each time a running search reports a new count
 * in its `checking` phase (views and fit measured before the plan is written).
 */
@Component({
	standalone: true,
	selector: 'app-ana-marketplace-search-watch',
	template: '',
	styles: [':host { display: none; }']
})
export class AnaMarketplaceSearchWatchComponent implements OnDestroy {
	@Output() searchEnded = new EventEmitter<AnaMarketplaceSearchEnd>();
	@Output() searchChecking = new EventEmitter<number>();

	private currentSearchId: string | null = null;
	private consecutiveFailures = 0;
	private endedReported = false;
	/** The last checking count reported, so a steady poll emits it once. */
	private reportedCheckingCount: number | null = null;
	private stopPolling = new Subject<void>();
	private unsubscriber = new Subject<void>();

	constructor(private anaAssistantService: AnaAssistantService) {}

	@Input() set searchId(value: string | null | undefined) {
		const nextSearchId = value ?? null;
		if (nextSearchId === this.currentSearchId) return;
		this.currentSearchId = nextSearchId;
		this.stopPolling.next();
		this.consecutiveFailures = 0;
		this.endedReported = false;
		this.reportedCheckingCount = null;
		if (nextSearchId) {
			this.startPolling(nextSearchId);
		}
	}

	ngOnDestroy(): void {
		this.stopPolling.next();
		this.stopPolling.complete();
		this.unsubscriber.next();
		this.unsubscriber.complete();
	}

	private startPolling(searchId: string): void {
		timer(0, POLL_INTERVAL_MS)
			.pipe(
				// exhaustMap: a slow read is never cancelled and re-issued.
				exhaustMap(() => this.pollOnce(searchId)),
				takeUntil(this.stopPolling),
				takeUntil(this.unsubscriber)
			)
			.subscribe(search => this.applyReading(search));
	}

	/** Resolves to null when the read could not be served. */
	private pollOnce(
		searchId: string
	): Observable<AnaMarketplaceSearchResponse | null> {
		return this.anaAssistantService
			.getMarketplaceSearch(searchId)
			.pipe(catchError(() => of(null)));
	}

	private applyReading(search: AnaMarketplaceSearchResponse | null): void {
		if (!search) {
			this.consecutiveFailures += 1;
			if (this.consecutiveFailures >= MAX_CONSECUTIVE_POLL_FAILURES) {
				this.reportEnded({ status: null, savedCount: 0 });
			}
			return;
		}
		this.consecutiveFailures = 0;
		if (ACTIVE_STATUSES.includes(search.status)) {
			this.reportChecking(search);
			return;
		}
		this.reportEnded({
			status: search.status,
			savedCount: search.saved_count ?? 0
		});
	}

	/** A missing `phase` is an older backend: collecting, nothing to report. */
	private reportChecking(search: AnaMarketplaceSearchResponse): void {
		if (search.phase !== 'checking' || search.checking_count == null) {
			return;
		}
		if (search.checking_count === this.reportedCheckingCount) return;
		this.reportedCheckingCount = search.checking_count;
		this.searchChecking.emit(search.checking_count);
	}

	/** Once per search: the host starts a conversation turn on it. */
	private reportEnded(end: AnaMarketplaceSearchEnd): void {
		this.stopPolling.next();
		if (this.endedReported) return;
		this.endedReported = true;
		this.searchEnded.emit(end);
	}
}
